<template>
  <sweet-modal
    ref="modal"
    :hide-close-button="false"
    width="900px"
  >
    <builderall-loader
      :loading="loading"
      :text="`${$t('ct-uploading-file')}`"
      carousel="true"
    >
      <div class="popup-header">
        <div class="popup-header-title">
          <img src="/static/images/icons/ai-html.svg" alt="AI HTML Upload">
          <span>{{  $t('ct-ai-html-upload') }}</span>
        </div>
      </div>

      <p class="popup-description ba-text-center">
        <span>{{ $t('ct-ai-html-upload-subtitle') }}</span>
      </p>

      <div
        class="upload-dropzone"
        :class="{ 'upload-dropzone--active': isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onDrop"
      >
        <div class="upload-dropzone-icon">
          <img src="/static/images/icons/upload-html.svg" alt="Upload">
        </div>
        <p>{{ $t('ct-ai-html-upload-dropzone-info') }}</p>
        <p>{{ $t('ct-or') }}</p>
        <button type="button" class="choose-file-btn" @click="$refs.fileInput.click()">
          {{ $t('ct-ai-html-upload-button-info') }}
        </button>
        <input
          ref="fileInput"
          type="file"
          accept=".html,.htm,.zip"
          class="d-none"
          @change="onFileSelected"
        >
        <p v-if="selectedFile" class="selected-file-name">{{ selectedFile.name }}</p>
      </div>

      <p class="upload-hint ba-text-center">{{ $t('ct-ai-html-upload-fileinfo') }}</p>

      <div v-if="errorMessage" class="error-box">
        <img class="error-box-icon" src="/static/images/icons/error-circle.svg" alt="Error">
        <div>
          <strong>{{ errorTitle }}</strong>
          <p>{{ errorMessage }}</p>
          <p>
            {{ copyPromptParts.before }}<a
              style="color: #0483FF;"
              href="#"
              @click.prevent="copyPrompt"
            >{{ copyPromptParts.link }}</a>{{ copyPromptParts.after }}
          </p>
        </div>
      </div>

      <div v-else class="important-box">
        <div class="important-box-icon">!</div>
        <div>
          <strong>{{ $t('ct-important') }}!</strong>
          <p>{{ $t('ct-ai-html-upload-warning') }}</p>
        </div>
      </div>

      <div class="popup-actions">
        <button type="button" class="cancel-link" @click="close">{{ $t('ct-cancel') }}</button>
        <button
          type="button"
          class="upload-btn"
          :disabled="!selectedFile || loading"
          @click="upload"
        >
          <img height="14" style="filter: brightness(0) invert(1);" src="/static/images/icons/ai-html.svg" alt="AI HTML Upload">
          {{ $t('ct-upload-and-analyze') }}
        </button>
      </div>
    </builderall-loader>
  </sweet-modal>
</template>

<script>
import { SweetModal } from 'sweet-modal-vue';
import { importStaticWebsite } from '../../../js/services/editor/website';
import { AI_ZIP_PROMPT_TEMPLATE } from '../../../assets/prompts/aiHtmlUploadZip';
import copyToClipboard from '../../../js/mixins/copyToClipboard';

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100MB — matches API max:102400

const UNKNOWN_REJECTED_FILE = 'a file that is not part of a static build';

export default {
  name: 'AiHtmlUploadPopup',
  mixins: [copyToClipboard],
  components: {
    SweetModal,
  },
  data() {
    return {
      loading: false,
      selectedFile: null,
      isDragging: false,
      website: null,
      errorMessage: '',
      unsupportedFilePath: '',
    };
  },
  computed: {
    errorTitle() {
      return this.$te('ct-oops') ? this.$t('ct-oops') : 'Oops!';
    },
    copyPromptParts() {
      const parts = this.$t('ct-ai-html-upload-copy-prompt').split('|');
      return {
        before: parts[0] || '',
        link: parts[1] || '',
        after: parts[2] || '',
      };
    },
  },
  beforeDestroy() {
    this.$root.$off('open-ai-html-upload', this.openWithWebsite);
  },
  mounted() {
    this.$root.$on('open-ai-html-upload', this.openWithWebsite);
  },
  methods: {
    openWithWebsite(website = null) {
      this.website = website;
      this.selectedFile = null;
      this.isDragging = false;
      this.errorMessage = '';
      this.unsupportedFilePath = '';
      this.$refs.modal.open();
    },
    open() {
      this.openWithWebsite(null);
    },
    close() {
      if (this.loading) return;
      this.selectedFile = null;
      this.website = null;
      this.errorMessage = '';
      this.unsupportedFilePath = '';
      this.$refs.modal.close();
    },
    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.setSelectedFile(file);
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      this.setSelectedFile(file);
      event.target.value = '';
    },
    isFileTooLarge(file) {
      return file && file.size > MAX_FILE_SIZE_BYTES;
    },
    notifyFileTooLarge() {
      this.errorMessage = `${this.$t('ct-upload-files-maxfile')}`;
    },
    setSelectedFile(file) {
      if (!file) return;

      const extension = file.name.split('.').pop().toLowerCase();

      if (!['html', 'htm', 'zip'].includes(extension))
      {
        this.errorMessage = this.$t('ct-ai-html-upload-file-error');
        return;
      }

      if (this.isFileTooLarge(file))
      {
        this.selectedFile = null;
        this.notifyFileTooLarge();
        return;
      }

      this.errorMessage = '';
      this.selectedFile = file;
    },
    upload() {
      if (!this.selectedFile || this.loading) return;

      if (this.isFileTooLarge(this.selectedFile))
      {
        this.selectedFile = null;
        this.notifyFileTooLarge();
        return;
      }

      this.errorMessage = '';
      this.loading = true;

      const isCreation = !(this.website && this.website.encrypted_id);

      const formData = new FormData();
      formData.append('file', this.selectedFile);

      if (!isCreation)
      {
        formData.append('website_id', this.website.encrypted_id);
      }

      importStaticWebsite(formData)
        .then((response) => {
          this.loading = false;
          if (response.data.success === false)
          {
            this.errorMessage = response.data.error || this.$t('ct-ai-html-upload-file-error');
            return;
          }

          this.$snotify.success(this.$t('ct-saved-successfully'));
          this.$emit('success', {
            website: response.data.website,
            isCreation,
          });
          this.close();
        })
        .catch((error) => {
          this.loading = false;
          let message = '';

          if (!error.response || error.response.status === 413) {
            message = 'The file exceeded the size limit or the connection was interrupted.';
          } else if (error.response.status === 422 && Object.hasOwn(error.response.data, 'errors') && Object.hasOwn(error.response.data.errors, 'file') && error.response.data.errors.file.length > 0) {
            message = error.response.data.errors.file[0] || '';
          } else {
            message = error.response && error.response.data && error.response.data.error
              ? error.response.data.error
              : '';
          }

          const matchFiles = message.match(/\(\s*([^()]+?)\s*\)/);
          this.unsupportedFilePath = matchFiles ? matchFiles[1] : '';

          this.errorMessage = (this.$t('ct-ai-html-upload-file-error') + ' ' + message).trim();
        })
    },
    buildPrompt() {
      return AI_ZIP_PROMPT_TEMPLATE.replace('{file_path}', this.unsupportedFilePath || UNKNOWN_REJECTED_FILE);
    },
    copyPrompt() {
      this.copyToClipboard(this.buildPrompt())
        .then(() => {
          this.$snotify.success(this.$t('ct-copyed-success'));
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.popup-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid rgba(77, 93, 113, 0.25);
  padding-bottom: 16px;
  margin-bottom: 16px;
}

.popup-header-title {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1;

  span {
    font-size: 28px;
    font-weight: 600;
    color: #111827;
  }
}

.close-btn,
.cancel-link {
  background: none;
  border: none;
  color: #4D5D71;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  cursor: pointer;
}

.popup-description {
  color: #54779E;
  font-size: 16px;
  margin-bottom: 16px;
  span {
    max-width: 450px;
    display: block;
    margin: 0 auto;
  }
}

.upload-dropzone {
  border: 1px dashed #01D47B;
  background: #FCF9FF;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;

  &--active {
    background: #F0FFF8;
  }

  p {
    margin: 0;
    color: #1E1E2D;
    font-size: 14px;
  }
}

.upload-dropzone-icon {
  font-size: 42px;
  color: #7E1AFD;
}

.choose-file-btn {
  border: 1px solid #01D47B;
  background: #fff;
  color: #01D47B;
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.selected-file-name {
  font-weight: 600;
  word-break: break-all;
}

.upload-hint {
  color: #54779E;
  font-size: 14px;
  margin: 8px 0 16px;
  text-align: left;
}

.important-box {
  display: flex;
  gap: 12px;
  background: #FFF5D8;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 24px;

  strong {
    display: block;
    margin-bottom: 4px;
    color: #111827;
  }

  p {
    margin: 0;
    color: #111827;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
  }
}

.error-box {
  display: flex;
  gap: 16px;
  background: #FEE0E2;
  border-radius: 6px;
  padding: 24px 30px;
  margin-bottom: 24px;

  strong {
    display: block;
    margin-bottom: 4px;
    color: #111827;
    font-size: 14px;
  }

  p {
    margin: 0;
    color: #111827;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-line;
    word-break: break-word;
  }
}

.error-box-icon {
  width: 25px;
  height: 25px;
  flex-shrink: 0;
}

.important-box-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #FACC15;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
}

.popup-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #01D47B;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/components/AiHtmlUploadPopup.vue